"use client";

import { NextIntlClientProvider } from "next-intl";
import { ZustandProvider } from "../../store";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@ui/ThemeProvider";
import { hasWindow } from "@lib/utils/hasWindow";
import { initializeKount } from "@lib/utils/kount/session";
import { useEffect } from "react";
import RootComponentWrapper from "@components/RootWrapper";
import { getQueryClient } from "@lib/helpers/useApi";
import { SessionProvider } from "next-auth/react";
import dynamic from "next/dynamic";
import { initializeDatadog } from "@lib/monitoring/initializeDatadog";

const ReactQueryDevtools = dynamic(() =>
    import("@tanstack/react-query-devtools").then((mod) => mod.ReactQueryDevtools)
);

const ContentfulLivePreviewProvider = dynamic(() =>
    import("@contentful/live-preview/react").then((mod) => mod.ContentfulLivePreviewProvider)
);

const useInitializeServices = () => {
    useEffect(() => {
        if (hasWindow) {
            initializeKount();
            initializeDatadog();
        }
    }, []);
};

interface ClientProvidersProps {
    children: React.ReactNode;
    locale: string;
    isPreviewMode: boolean;
}

export default function ClientProviders({ children, locale, isPreviewMode }: ClientProvidersProps) {
    useInitializeServices();
    const queryClient = getQueryClient();

    return (
        <NextIntlClientProvider locale={locale}>
            <SessionProvider>
                <QueryClientProvider client={queryClient}>
                    <ZustandProvider>
                        <RootComponentWrapper>
                            <ThemeProvider>
                                {isPreviewMode ? (
                                    <ContentfulLivePreviewProvider
                                        //ContentfulLivePreviewProvider doesn't support multilocale, and we are previewing only EN, so we need to hardcode it
                                        locale={"en"}
                                        enableLiveUpdates={isPreviewMode}
                                        enableInspectorMode={isPreviewMode}
                                    >
                                        {children}
                                    </ContentfulLivePreviewProvider>
                                ) : (
                                    children
                                )}
                                <ReactQueryDevtools initialIsOpen={false} />
                            </ThemeProvider>
                        </RootComponentWrapper>
                    </ZustandProvider>
                </QueryClientProvider>
            </SessionProvider>
        </NextIntlClientProvider>
    );
}
